import axios from 'axios';
import { bypassNavigationBlock } from '../hooks/useNavigationBlock';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_URL
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and maintenance mode
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        bypassNavigationBlock();
        window.location.href = '/login';
      }
    }
    // Maintenance mode: broadcast event so App can show maintenance screen
    if (error.response?.status === 503 && error.response?.data?.maintenanceMode) {
      window.dispatchEvent(new CustomEvent('maintenance-mode', { detail: true }));
    }
    return Promise.reject(error);
  }
);

export default api;

// API functions

export interface Request {
  id: string;
  requestId: string;
  title: string;
  description: string;
  category: 'PERSONNEL' | 'MATERIAL' | 'MEDEVAC' | 'TRANSPORT' | 'OTHER' | 'NEWS';
  urgency: 'HIGH' | 'NORMAL';
  status: 'DRAFT' | 'UNDER_REVIEW' | 'PUBLISHED' | 'UNDER_SELECTION' | 'ACCEPTED' | 'PLANNED' | 'IN_TRANSIT' | 'COMPLETED' | 'CLOSED' | 'WITHDRAWN';
  answerBy?: string;
  readyAtHub?: string;
  medevacRemarks?: string;
  excelFile?: string;
  attachments?: any[];
  pickupStartPointId?: string;
  pickupStartPoint?: StartPoint;
  exitPointId?: string;
  exitPoint?: StartPoint;
  createdAt: string;
  publishedAt?: string;
  creatorId: string;
  creator: {
    id: string;
    nation: string;
    nationCode?: string;
    flagImage?: string;
  };
  parentRequestId?: string;
  parentRequest?: { id: string; requestId: string; title: string };
  followupRequests?: { id: string; requestId: string; title: string; status: string; category: string; _count: { patients: number } }[];
  patients?: Patient[];
  comments?: Comment[];
  offers?: Offer[];
}

// Patient Management System Types
export type Gender = 'MALE' | 'FEMALE' | 'TBA';
export type PatientStatus = 'CIVIL' | 'MILITARY' | 'TBA';
export type TransportMode = 'SEATED' | 'LYING' | 'TBA';
export type DependencyLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'TBA';
export type Priority = 'P1' | 'P2' | 'P3' | 'TBA';
export type MedicalRequirementType =
  | 'INTENSIVE_CARE'
  | 'SURGERY'
  | 'ONCOLOGY'
  | 'DIALYSIS'
  | 'BURN_UNIT'
  | 'NURSING'
  | 'MATERNITY'
  | 'REHABILITATION';
export type DocumentType =
  | 'LABORATORY_RESULTS'
  | 'IMAGING_REPORT'
  | 'MEDICAL_HISTORY'
  | 'TREATMENT_PLAN'
  | 'OTHER';

export interface PatientMedicalRequirement {
  id: string;
  patientId: string;
  requirementType: MedicalRequirementType;
  createdAt: string;
}

export interface PatientDocument {
  id: string;
  patientId: string;
  documentType: DocumentType;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  title?: string;
  description?: string;
  uploadedAt: string;
  uploadedBy?: string;
}

export interface Patient {
  id: string;

  // Unique reference
  patientGuid: string;

  // Patient identification (after request link)
  patientId?: string;
  sendingNation?: string;

  // Sensitive data (NOT visible for Nation users)
  patientName?: string;
  patientFirstname?: string;
  patientDob?: string;

  // Public/pseudonymized data
  patientInitials: string;
  patientAge: number;
  patientGender: Gender;
  patientStatus: PatientStatus;
  nationality?: string;

  // Medical data
  medicalPrimaryConditionId: string;
  medicalPrimaryConditionDiagnosis: string;
  medicalSecondaryConditionText?: string;
  medicalConditionDescription?: string;
  medicalTransport: TransportMode;
  medicalDependency: DependencyLevel;
  medicalPriority: Priority;

  // Vital signs
  vitalBloodPressureSystolic?: number;
  vitalBloodPressureDiastolic?: number;
  vitalHeartRate?: number;
  vitalRespiratoryRate?: number;
  vitalTemperature?: number;
  vitalSpo2?: number;
  vitalGcs?: number;
  vitalPainScale?: number;

  // Transport info
  transportSourceCountry?: string;
  transportSourceFacility?: string;
  transportExitPoint?: string;
  transportDestinationCountry?: string;

  // Companions
  patientCompanionsCount: number;
  patientCompanionsText?: string;

  // Narrative
  patientBackground?: string;
  patientCurrentSituation?: string;
  narrativeClinical?: string;
  narrativeAdditionalRemarks?: string;

  // Relations
  requestId?: string;
  request?: Request;
  documents?: PatientDocument[];
  medicalRequirements?: PatientMedicalRequirement[];

  // Metadata
  importedAt: string;
  importedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  id: string;
  content: string;
  createdAt: string;
  user: {
    id: string;
    nation: string;
    nationCode?: string;
    flagImage?: string;
  };
}

// Offer System Types
export type OfferType = 'TREATMENT' | 'TRANSPORT' | 'BOTH' | 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY' | 'TRANSPORT_EXTENSION';
export type OfferStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED';
export type OfferTransportMode = 'INCLUDED_BY_OFFERING_NATION' | 'PROVIDED_BY_PARTNER_NATION' | 'OPEN';
export type ChainStatus = 'COMPLETE' | 'PARTIAL';

export interface OfferPatient {
  id: string;
  offerId: string;
  patientId: string;
  offerType: OfferType;
  transportMode?: string;
  patient: Patient;
}

export interface Offer {
  id: string;
  requestId: string;
  userId: string;
  offerType: OfferType;
  status: OfferStatus;

  // UCPM Hub for receiving nation
  receivingStartPointId?: string;
  receivingStartPoint?: StartPoint;
  treatmentCapacity?: number;
  treatmentNotes?: string;

  // Transport Details
  departureHubId?: string;
  departureHub?: Hub;
  arrivalHubId?: string;
  arrivalHub?: Hub;
  assetId?: string;
  asset?: Asset;
  departureDate?: string;
  transportNotes?: string;

  // Chain architecture fields
  transportMode?: OfferTransportMode;
  transportProviderNationId?: string;
  transportAgreementNote?: string;
  referencesOfferId?: string;
  extensionOffers?: Offer[];

  // General
  comment?: string;

  createdAt: string;
  updatedAt: string;
  acceptedAt?: string;
  rejectedAt?: string;

  user: {
    id: string;
    nation: string;
    nationCode?: string;
    flagImage?: string;
    email: string;
  };

  offerPatients: OfferPatient[];
}

// Mission Chain Types
export interface MissionChain {
  id: string;
  requestId: string;
  treatmentOfferId: string;
  treatmentNation: string;
  transportOfferId?: string;
  transportNation?: string;
  status: ChainStatus;
  accepted: boolean;
  acceptedAt?: string;
  patients: { patientId: string; patient: Patient }[];
  treatmentOffer?: Offer;
  transportOffer?: Offer;
  missions?: { id: string; missionId: string; missionType: string; status: string }[];
}

export interface ChainCandidate {
  treatmentOfferId: string;
  treatmentNation: string;
  treatmentNationFlag?: string | null;
  treatmentOfferType: OfferType;
  transportOfferId?: string | null;
  transportNation?: string | null;
  transportNationFlag?: string | null;
  receivingStartPoint?: { id: string; name: string } | null;
  status: ChainStatus;
}

export interface ChainPatientAnalysis {
  patientId: string;
  patient: {
    id: string;
    patientId: string | null;
    patientInitials: string;
    patientAge: number;
    patientGender: string;
    medicalPriority: string;
    medicalPrimaryConditionDiagnosis: string;
    medicalTransport: string;
    patientCompanionsCount: number;
  };
  chains: {
    complete: ChainCandidate[];
    partial: ChainCandidate[];
  };
}

export interface ChainAnalysis {
  requestId: string;
  patientAnalyses: ChainPatientAnalysis[];
  summary: {
    totalPatients: number;
    patientsWithComplete: number;
    patientsWithPartialOnly: number;
    patientsWithNoOffer: number;
  };
  existingChains: {
    id: string;
    treatmentNation: string;
    treatmentNationFlag?: string | null;
    transportNation?: string | null;
    transportNationFlag?: string | null;
    status: ChainStatus;
    patientIds: string[];
  }[];
  lockedPatientIds: string[];
}

export interface ChainSelection {
  patientId: string;
  treatmentOfferId: string;
  transportOfferId?: string | null;
}

// Transport Planning Interfaces

export type UcpmClassification = 'UCPM_TRANSIT_HUB' | 'UCPM_EXIT_HUB';

export interface StartPoint {
  id: string;
  name: string;
  types: ('AIRPORT' | 'TRAIN_STATION' | 'SEAPORT' | 'LOCATION')[]; // Array to support multiple types
  ucpmClassification: UcpmClassification; // UCPM classification
  operator?: string; // Operator name (English)
  contactDetails?: string; // Contact details (phone, email, etc.)
  latitude: number;
  longitude: number;
  nation?: string;
  description?: string;
  isActive: boolean;
  assignedNations?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Asset {
  id: string;
  name: string;
  type: 'TRAIN' | 'SHIP' | 'AIRCRAFT' | 'BUS' | 'AMBULANCE' | 'DRONE' | 'HELICOPTER' | 'FERRY' | 'OTHER';
  capacitySeated: number;
  capacityLying: number;
  capacityIntensive: number;
  nation: string;
  description?: string;
  isActive: boolean;
  averageSpeedKmh?: number;
  currentLat?: number;
  currentLng?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Hub {
  id: string;
  name: string;
  type: 'HOSPITAL' | 'COORDINATION_CENTER' | 'STAGING_AREA' | 'OTHER';
  latitude: number;
  longitude: number;
  nation: string;
  capacity?: number;
  description?: string;
  offeredTreatments?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  hubAssignments?: HubAssignment[];
}

export interface HubAssignment {
  id: string;
  userId: string;
  hubId: string;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    nation: string;
    role: string;
  };
  hub?: Hub;
}

export interface Transport {
  id: string;
  requestId: string;
  startPointId: string;
  assetId: string;
  hubId: string;
  plannedBy: string;
  departureTime?: string;
  arrivalTime?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  request?: Request;
  startPoint?: StartPoint;
  asset?: Asset;
  hub?: Hub;
  planner?: {
    id: string;
    nation: string;
    email: string;
  };
}

export interface AssetContract {
  id: string;
  providerNation: string;
  consumerNation: string;
  assetTypes: string[];
  startDate: string;
  endDate: string;
  description?: string;
  isActive: boolean;
  createdBy: string;
  creator?: User;
  createdAt: string;
  updatedAt: string;
}

export interface AssetAvailabilityWindow {
  from: string;
  to: string;
}

export interface AssetConflict {
  missionId: string;
  missionName: string;
  from: string;
  to: string;
}

export interface AssetAvailabilityCheck {
  available: boolean;
  conflicts: AssetConflict[];
}

export interface ETAResult {
  stopId: string;
  estimatedArrival: string | null;
  estimatedDeparture: string | null;
}

export interface DayTemplateNationPermission {
  id: string;
  dayTemplateId: string;
  nation: string;
  canCreateAssets: boolean;
  allowedAssetTypes: string[];
  createdAt: string;
  updatedAt: string;
}

export const requestsApi = {
  getAll: (params?: any) => api.get<Request[]>('/api/requests', { params }),
  getById: (id: string) => api.get<Request>(`/api/requests/${id}`),
  create: (data: Partial<Request>) => api.post<Request>('/api/requests', data),

  // NEW: Create request with Excel file (multipart/form-data)
  createWithExcel: (data: Partial<Request>, excelFile: File) => {
    const formData = new FormData();
    formData.append('title', data.title || '');
    formData.append('description', data.description || '');
    formData.append('category', data.category || '');
    formData.append('urgency', data.urgency || 'NORMAL');
    if (data.answerBy) formData.append('answerBy', data.answerBy);
    formData.append('readyAtHub', data.readyAtHub || '');
    formData.append('medevacRemarks', data.medevacRemarks || '');
    formData.append('patientExcel', excelFile);

    return api.post<{
      request: Request;
      patients: {
        imported: number;
        failed: number;
        errors: Array<{ row: number; error: string }>;
      } | null;
    }>('/api/requests', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Create request with attachments for Personnel/Material/Other
  createWithAttachments: (data: Partial<Request>, attachments?: File[]) => {
    const formData = new FormData();
    formData.append('title', data.title || '');
    formData.append('description', data.description || '');
    formData.append('category', data.category || '');
    formData.append('urgency', data.urgency || 'NORMAL');
    if (data.answerBy) formData.append('answerBy', data.answerBy);

    // Add attachments if provided
    if (attachments && attachments.length > 0) {
      attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }

    return api.post<{
      request: Request;
    }>('/api/requests', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  update: (id: string, data: Partial<Request>) => api.put<Request>(`/api/requests/${id}`, data),
  submit: (id: string) => api.post<Request>(`/api/requests/${id}/submit`),
  publish: (id: string) => api.post<Request>(`/api/requests/${id}/publish`),
  sendReminder: (id: string) => api.post<{ message: string; recipientCount: number }>(`/api/requests/${id}/remind`),
  withdraw: (id: string) => api.post<Request>(`/api/requests/${id}/withdraw`),
  revertToDraft: (id: string) => api.post(`/api/requests/${id}/revert-to-draft`),
  deleteRequest: (id: string) => api.delete(`/api/requests/${id}`),
  reject: (id: string, message: string) => api.post<{ message: string; request: Request }>(`/api/requests/${id}/reject`, { message }),
  startSelection: (id: string) => api.post<Request>(`/api/requests/${id}/start-selection`),
  reopen: (id: string) => api.post<Request>(`/api/requests/${id}/reopen`),
  completeSelection: (id: string) => api.post<Request>(`/api/requests/${id}/complete-selection`),
  updateStatus: (id: string, status: string) => api.patch<Request>(`/api/requests/${id}/status`, { status }),
  createTransport: (requestId: string, data: { startPointId: string; assetId: string; hubId: string; departureTime?: string; arrivalTime?: string; notes?: string }) =>
    api.post<{ message: string; transport: Transport }>(`/api/requests/${requestId}/transport`, data),
  uploadExcel: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ message: string; filePath: string; preview?: PatientPreview[] }>(`/api/requests/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmImport: (id: string) => api.post<{ message: string; patientCount: number; request: Request }>(`/api/requests/${id}/confirm-import`),
  getPatientOffers: (requestId: string) => api.get<PatientOfferStats[]>(`/api/requests/${requestId}/patient-offers`),
  getPatientAssignments: (requestId: string) => api.get<PatientAssignment[]>(`/api/requests/${requestId}/patient-assignments`),
  createFollowup: (requestId: string, patientIds: string[], title?: string, description?: string) =>
    api.post<{ id: string; requestId: string; patientCount: number }>(
      `/api/requests/${requestId}/create-followup`,
      { patientIds, title, description }
    ),
  createTransportFollowup: (requestId: string, patients: { patientId: string; treatmentNation: string; treatmentHubName: string }[]) =>
    api.post<{ id: string; requestId: string; patientCount: number }>(
      `/api/requests/${requestId}/create-transport-followup`,
      { patients }
    ),
  excludePatients: (requestId: string, patientIds: string[]) =>
    api.post<{ message: string; count: number }>(
      `/api/requests/${requestId}/exclude-patients`,
      { patientIds }
    ),
  exportUnmatchedPatientsExcel: (requestId: string, patientIds: string[]) =>
    api.get(`/api/requests/${requestId}/unmatched-patients/excel`, {
      params: { patientIds: patientIds.join(',') },
      responseType: 'blob'
    }),
  addPatientsToRequest: (requestId: string, patientIds: string[], sourceRequestId?: string) =>
    api.post<{ message: string; count: number; title: string }>(
      `/api/requests/${requestId}/add-patients`,
      { patientIds, sourceRequestId }
    ),

  importPatientsExcel: (requestId: string, excelFile: File, excludeRows?: number[]) => {
    const formData = new FormData();
    formData.append('patientExcel', excelFile);
    if (excludeRows && excludeRows.length > 0) {
      formData.append('excludeRows', JSON.stringify(excludeRows));
    }
    return api.post<{
      message: string;
      imported: number;
      failed: number;
      warnings: number;
      errors: Array<{ row: number | string; message: string; level?: string }>;
      title: string;
    }>(`/api/requests/${requestId}/import-patients-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // EU Direct-Assign Transport for TRANSPORT requests
  directAssignTransport: (requestId: string, data: { patientIds: string[]; customAssetName?: string; departureDate?: string; transportNotes?: string }) =>
    api.post<{ success: boolean; offerId: string; patientCount: number }>(
      `/api/requests/${requestId}/direct-assign-transport`,
      data
    )
};

export interface PatientOfferNation {
  nation: string;
  flagImage: string | null;
  offerType: string;
  label: string;
  hubName: string | null;
  transportProviderNationId?: string | null;
  treatmentNation?: string | null;
  transportNation?: string | null;
}

export interface PatientOfferStats {
  patientId: string;
  offerCount: number;
  bestOfferType: 'BOTH' | 'TREATMENT' | 'TRANSPORT' | 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY' | 'NONE';
  offersByType: {
    BOTH: number;
    TREATMENT: number;
    TRANSPORT: number;
    TREATMENT_AND_TRANSPORT?: number;
    TREATMENT_ONLY?: number;
    TRANSPORT_EXTENSION?: number;
  };
  offersByNation?: PatientOfferNation[];
}

export interface PatientAssignment {
  patientId: string;
  treatment: { nation: string; flagImage: string | null; hubName: string | null } | null;
  transport: { nation: string; flagImage: string | null } | null;
}

export interface PatientPreview {
  patientId: string;
  birthDate?: string;
  gender?: string;
  pseudonym?: string;
  mainDiagnosis?: string;
  exitPoint?: string;
  targetCountry?: string;
  remarks?: string;
}

export const commentsApi = {
  getByRequest: (requestId: string) => api.get<Comment[]>(`/api/comments/request/${requestId}`),
  create: (data: { requestId: string; content: string }) => api.post<Comment>('/api/comments', data),
  update: (id: string, content: string) => api.put<Comment>(`/api/comments/${id}`, { content }),
  delete: (id: string) => api.delete(`/api/comments/${id}`)
};

export const offersApi = {
  getByRequest: (requestId: string, includeExtensions = false) =>
    api.get<Offer[]>(`/api/offers/request/${requestId}${includeExtensions ? '?includeExtensions=true' : ''}`),
  getMyOffers: (requestId: string) => api.get<Offer[]>(`/api/offers/request/${requestId}/my-offers`),
  create: (data: {
    requestId: string;
    offerType?: OfferType;
    patientIds?: string[];
    patientOffers?: { patientId: string; offerType: OfferType; transportMode?: OfferTransportMode; transportProviderNationId?: string; transportAgreementNote?: string }[];
    receivingStartPointId?: string;
    comment?: string;
    description?: string;
    // Chain architecture fields
    transportMode?: OfferTransportMode;
    transportProviderNationId?: string;
    transportAgreementNote?: string;
    referencesOfferId?: string;
  }) => api.post<{ message: string; offer: Offer }>('/api/offers', data),
  update: (id: string, data: {
    offerType?: OfferType;
    patientIds?: string[];
    patientOffers?: { patientId: string; offerType: OfferType; transportMode?: OfferTransportMode; transportProviderNationId?: string; transportAgreementNote?: string }[];
    receivingStartPointId?: string;
    comment?: string;
    description?: string;
    transportMode?: OfferTransportMode;
    transportProviderNationId?: string;
    transportAgreementNote?: string;
  }) => api.put<{ message: string; offer: Offer }>(`/api/offers/${id}`, data),
  accept: (id: string) => api.post<{ message: string; offer: Offer }>(`/api/offers/${id}/accept`),
  reject: (id: string, reason?: string) => api.post<{ message: string; offer: Offer }>(`/api/offers/${id}/reject`, { reason }),
  withdraw: (id: string) => api.post<{ message: string; offer: Offer }>(`/api/offers/${id}/withdraw`),
  delete: (id: string) => api.delete<{ message: string }>(`/api/offers/${id}`),
  sendTransportIntent: (data: {
    requestId: string;
    patientIds: string[];
    targetNations: string[];
    message?: string;
  }) => api.post<{ message: string }>('/api/offers/transport-intent', data)
};

export const chainsApi = {
  analyze: (requestId: string) =>
    api.get<ChainAnalysis>(`/api/chains/request/${requestId}/analyze`),
  save: (requestId: string, selections: ChainSelection[]) =>
    api.post<{ message: string; chainIds: string[] }>(`/api/chains/request/${requestId}/save`, { selections }),
  accept: (requestId: string, allowPartial = false) =>
    api.post<{ message: string; missions: any[]; requestStatus: string }>(`/api/chains/request/${requestId}/accept`, { allowPartial }),
  getByRequest: (requestId: string) =>
    api.get<MissionChain[]>(`/api/chains/request/${requestId}`),
  deleteDrafts: (requestId: string) =>
    api.delete<{ message: string }>(`/api/chains/request/${requestId}/drafts`)
};

export const usersApi = {
  getAll: () => api.get('/api/users'),
  getNations: () => api.get<{ code: string; name: string; flagImage: string; hasUser: boolean }[]>('/api/users/nations'),
  getById: (id: string) => api.get(`/api/users/${id}`),
  getDetails: (id: string) => api.get<UserDetails>(`/api/users/${id}/details`),
  create: (data: any) => api.post('/api/users', data),
  update: (id: string, data: any) => api.put(`/api/users/${id}`, data),
  delete: (id: string) => api.delete(`/api/users/${id}`),
  resetPassword: (id: string) => api.post(`/api/users/${id}/reset-password`),
  importUsers: (file: File) => {
    const formData = new FormData();
    formData.append('usersExcel', file);
    return api.post('/api/users/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportCredentialsPdf: () => api.get('/api/users/export/credentials-pdf', { responseType: 'blob' })
};

export interface AuditLog {
  id: string;
  userId?: string;
  userEmail?: string;
  userNation?: string;
  userDisplayName?: string;
  userRoles?: {
    isAdmin: boolean;
    isExcon: boolean;
    isEU: boolean;
    isPlanner: boolean;
    isHub: boolean;
    isNation: boolean;
  };
  action: string;
  resourceType?: string;
  resourceId?: string;
  details?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export const auditLogsApi = {
  getAll: (params?: {
    userId?: string;
    action?: string;
    resourceType?: string;
    resourceId?: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
    offset?: number;
  }) => api.get<{ logs: AuditLog[]; total: number }>('/api/audit-logs', { params }),
  getForResource: (type: string, id: string) =>
    api.get<{ logs: AuditLog[]; total: number }>(`/api/audit-logs/resource/${type}/${id}`)
};

// Transport Planning APIs

export const assetsApi = {
  getAll: (params?: { nation?: string; type?: string; isActive?: boolean }) =>
    api.get<Asset[]>('/api/assets', { params }),
  getById: (id: string) => api.get<Asset>(`/api/assets/${id}`),
  create: (data: Partial<Asset>) => api.post<Asset>('/api/assets', data),
  update: (id: string, data: Partial<Asset>) => api.put<Asset>(`/api/assets/${id}`, data),
  delete: (id: string) => api.delete(`/api/assets/${id}`)
};

export const assetContractsApi = {
  getAll: (params?: { nation?: string }) =>
    api.get<AssetContract[]>('/api/asset-contracts', { params }),
  getById: (id: string) =>
    api.get<AssetContract>(`/api/asset-contracts/${id}`),
  create: (data: Omit<AssetContract, 'id' | 'createdBy' | 'creator' | 'createdAt' | 'updatedAt'>) =>
    api.post<AssetContract>('/api/asset-contracts', data),
  update: (id: string, data: Partial<AssetContract>) =>
    api.put<AssetContract>(`/api/asset-contracts/${id}`, data),
  delete: (id: string) =>
    api.delete(`/api/asset-contracts/${id}`),
  getAvailableAssets: (nation: string) =>
    api.get<(Asset & { contract: AssetContract })[]>(`/api/asset-contracts/nation/${nation}/available-assets`),
};

export const assetAvailabilityApi = {
  getAvailability: (assetId: string, from: string, to: string) =>
    api.get<{ freeWindows: AssetAvailabilityWindow[]; busyPeriods: AssetAvailabilityWindow[] }>(
      `/api/assets/${assetId}/availability`, { params: { from, to } }
    ),
  checkAvailability: (assetId: string, departure: string, arrival: string) =>
    api.get<AssetAvailabilityCheck>(
      `/api/assets/${assetId}/check-availability`, { params: { departure, arrival } }
    ),
  getMissionAvailability: (params: { missionId?: string; nation?: string; from?: string; to?: string }) =>
    api.get<{
      grouped: Record<string, { asset: Asset; available: boolean; busyUntil: string | null; nextAvailableAt: string | null; currentMissionId: string | null }[]>;
      summary: { total: number; available: number; busy: number };
    }>('/api/assets/mission-availability', { params }),
  getRoundTripTime: (assetId: string, departurePointId: string, destinationPointId: string) =>
    api.get<{ outboundMinutes: number; returnMinutes: number; turnaroundMinutes: number; totalMinutes: number; nextAvailableAt: string }>(
      `/api/assets/${assetId}/round-trip-time`, { params: { departurePointId, destinationPointId } }
    ),
};

export const startPointsApi = {
  getAll: (params?: { nation?: string; type?: string; isActive?: boolean }) =>
    api.get<StartPoint[]>('/api/startpoints', { params }),
  getById: (id: string) => api.get<StartPoint>(`/api/startpoints/${id}`),
  getByNation: (nation: string) => api.get<StartPoint[]>(`/api/startpoints/by-nation/${nation}`),
  create: (data: Partial<StartPoint>) => api.post<StartPoint>('/api/startpoints', data),
  update: (id: string, data: Partial<StartPoint>) => api.put<StartPoint>(`/api/startpoints/${id}`, data),
  delete: (id: string) => api.delete(`/api/startpoints/${id}`),
  getNations: (startPointId: string) =>
    api.get<{ id: string; startPointId: string; nation: string; createdAt: string; updatedAt: string }[]>(`/api/startpoints/${startPointId}/nations`),
  assignNation: (startPointId: string, nation: string) =>
    api.post(`/api/startpoints/${startPointId}/nations`, { nation }),
  unassignNation: (startPointId: string, nation: string) =>
    api.delete(`/api/startpoints/${startPointId}/nations/${nation}`)
};

export const hubsApi = {
  getAll: (params?: { nation?: string; type?: string; isActive?: boolean }) =>
    api.get<Hub[]>('/api/hubs', { params }),
  getById: (id: string) => api.get<Hub>(`/api/hubs/${id}`),
  create: (data: Partial<Hub>) => api.post<Hub>('/api/hubs', data),
  update: (id: string, data: Partial<Hub>) => api.put<Hub>(`/api/hubs/${id}`, data),
  delete: (id: string) => api.delete(`/api/hubs/${id}`),
  assignUser: (hubId: string, userId: string) =>
    api.post<HubAssignment>(`/api/hubs/${hubId}/assign`, { userId }),
  unassignUser: (hubId: string, userId: string) =>
    api.delete(`/api/hubs/${hubId}/assign/${userId}`),
  getNations: (hubId: string) =>
    api.get<{ id: string; hubId: string; nation: string; createdAt: string; updatedAt: string }[]>(`/api/hubs/${hubId}/nations`),
  assignNation: (hubId: string, nation: string) =>
    api.post(`/api/hubs/${hubId}/nations`, { nation }),
  assignNationsBulk: (hubId: string, nations: string[]) =>
    api.post(`/api/hubs/${hubId}/nations/bulk`, { nations }),
  unassignNation: (hubId: string, nation: string) =>
    api.delete(`/api/hubs/${hubId}/nations/${nation}`)
};

export const transportsApi = {
  getAll: (params?: { status?: string; nation?: string }) =>
    api.get<Transport[]>('/api/transports', { params }),
  getByRequestId: (requestId: string) => api.get<Transport>(`/api/transports/request/${requestId}`),
  create: (data: Partial<Transport>) => api.post<Transport>('/api/transports', data),
  update: (id: string, data: Partial<Transport>) => api.put<Transport>(`/api/transports/${id}`, data),
  start: (id: string) => api.post<{ message: string; transport: Transport }>(`/api/transports/${id}/start`),
  complete: (id: string) => api.post<{ message: string }>(`/api/transports/${id}/complete`)
};

// Schedule & Booking Interfaces

export interface ScheduleStop {
  id: string;
  scheduleId: string;
  stopOrder: number;
  startPointId?: string;
  hubId?: string;
  locationName: string;
  locationType: 'STARTPOINT' | 'HUB';
  arrivalTime?: string;
  departureTime?: string;
  latitude?: number;
  longitude?: number;
  createdAt: string;
  startPoint?: StartPoint;
  hub?: Hub;
}

export interface Schedule {
  id: string;
  scheduleId: string;
  assetId: string;
  name: string;
  description?: string;
  status: 'PLANNED' | 'ACTIVE' | 'DEPARTED' | 'COMPLETED' | 'CANCELLED';
  bookingMode?: 'FREE' | 'COORDINATED'; // FREE: freely bookable, COORDINATED: requires central flight booker
  capacitySeated: number;
  capacityLying: number;
  capacityIntensive: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  asset?: Asset;
  creator?: {
    id: string;
    username?: string;
    nation: string;
    email: string;
  };
  stops?: ScheduleStop[];
  bookings?: TransportBooking[];
  _count?: {
    bookings: number;
  };
  capacityPerSegment?: SegmentCapacity[];
}

export interface SegmentCapacity {
  fromStopOrder: number;
  toStopOrder: number;
  fromLocation: string;
  toLocation: string;
  usedSeated: number;
  usedLying: number;
  usedIntensive: number;
  availableSeated: number;
  availableLying: number;
  availableIntensive: number;
}

export interface TransportBooking {
  id: string;
  transportId?: string;
  scheduleId?: string;
  requestId: string;
  boardingStopOrder: number;
  exitStopOrder: number;
  seatedCount: number;
  lyingCount: number;
  intensiveCount: number;
  bookedBy: string;
  bookedByNation: string;
  createdAt: string;
  updatedAt: string;
  schedule?: Schedule;
  transport?: Transport;
  request?: {
    id: string;
    requestId: string;
    title: string;
    status: string;
    category: string;
  };
  bookedByUser?: {
    id: string;
    username?: string;
    nation: string;
    email: string;
  };
}

export interface AvailableScheduleMatch {
  schedule: Schedule;
  destinationStop?: ScheduleStop;
  availableCapacity: {
    seated: number;
    lying: number;
    intensive: number;
  };
  matchesRequirement: boolean;
}

// Schedule & Booking APIs

export const schedulesApi = {
  getAll: (params?: { status?: string; assetId?: string; nation?: string }) =>
    api.get<Schedule[]>('/api/schedules', { params }),
  getById: (id: string) => api.get<Schedule>(`/api/schedules/${id}`),
  create: (data: {
    scheduleId: string;
    assetId: string;
    name: string;
    description?: string;
    capacitySeated?: number;
    capacityLying?: number;
    capacityIntensive?: number;
    stops: Array<{
      startPointId?: string;
      hubId?: string;
      locationName: string;
      locationType: 'STARTPOINT' | 'HUB';
      arrivalTime?: string;
      departureTime?: string;
      latitude?: number;
      longitude?: number;
    }>;
  }) => api.post<Schedule>('/api/schedules', data),
  update: (id: string, data: Partial<Schedule>) => api.put<Schedule>(`/api/schedules/${id}`, data),
  cancel: (id: string) => api.delete<{ message: string; schedule: Schedule }>(`/api/schedules/${id}`),
  searchAvailable: (params: {
    destinationHubId: string;
    requiredSeated?: number;
    requiredLying?: number;
    requiredIntensive?: number;
  }) => api.get<AvailableScheduleMatch[]>('/api/schedules/available/search', { params })
};

export const bookingsApi = {
  getAll: (params?: { scheduleId?: string; requestId?: string; nation?: string }) =>
    api.get<TransportBooking[]>('/api/bookings', { params }),
  getById: (id: string) => api.get<TransportBooking>(`/api/bookings/${id}`),
  create: (data: {
    scheduleId: string;
    requestId: string;
    boardingStopOrder: number;
    exitStopOrder: number;
    seatedCount?: number;
    lyingCount?: number;
    intensiveCount?: number;
  }) => api.post<TransportBooking>('/api/bookings', data),
  cancel: (id: string) => api.delete<{ message: string }>(`/api/bookings/${id}`)
};

// Statistics API
export interface DashboardStatistics {
  overview: {
    totalRequests: number;
    totalUsers: number;
    totalOffers: number;
    totalComments: number;
    recentRequests: number;
    totalSchedules: number;
    activeSchedules: number;
    totalBookings: number;
  };
  breakdown: {
    byStatus: Array<{ status: string; count: number }>;
    byCategory: Array<{ category: string; count: number }>;
    byUrgency: Array<{ urgency: string; count: number }>;
    schedulesByStatus: Array<{ status: string; count: number }>;
  };
  personal: {
    myRequests: number;
    myOffers: number;
  };
}

export interface DetailedStatistics {
  requestsOverTime: Array<{
    date: string;
    count: number;
    byStatus: { [status: string]: number };
    byCategory: { [category: string]: number };
  }>;
  topContributors: Array<{
    userId: string;
    nation: string;
    offerCount: number;
  }>;
  averageResponseTime: number | null;
  completionRate: number;
}

export interface ExconDailyDay {
  date: string;
  requests: number;
  patientsTotal: number;
  avgPatientsPerRequest: number;
  priority: Record<string, number>;
  dependency: Record<string, number>;
  transportMethod: Record<string, number>;
  requestingNations: Record<string, number>;
  offeringNations: Record<string, number>;
  offers: number;
  offersAccepted: number;
  offersRejected: number;
  offersPending: number;
  offersByType: Record<string, number>;
  patientsEvacuated: number;
  patientsEvacuatedPercent: number;
  patientsPlanned: number;
  patientsPlannedPercent: number;
  patientsAssigned: number;
  patientsUnassigned: number;
}

export interface ExconDailyStats {
  generatedAt: string;
  days: ExconDailyDay[];
  totals: {
    requests: number;
    patientsTotal: number;
    offers: number;
    offersAccepted: number;
    offersRejected: number;
    offersPending: number;
    offersByType: Record<string, number>;
    patientsEvacuated: number;
    patientsEvacuatedPercent: number;
    patientsPlanned: number;
    patientsPlannedPercent: number;
    patientsAssigned: number;
    patientsUnassigned: number;
  };
}

export interface ExconSnapshotBreakdown {
  priority?: Record<string, number>;
  dependency?: Record<string, number>;
  transportMethod?: Record<string, number>;
  offersAccepted?: number;
  offersRejected?: number;
  offersPending?: number;
  patientsEvacuated?: number;
  patientsPlanned?: number;
  patientsAssigned?: number;
  patientsUnassigned?: number;
  requestingNations?: Record<string, number>;
  offeringNations?: Record<string, number>;
}

export interface ExconSnapshot {
  id: string;
  dayLabel: string;
  templateName: string;
  status: string;
  activatedAt: string;
  completedAt: string | null;
  createdAt: string;
  totalRequests: number;
  totalOffers: number;
  totalMissions: number;
  totalPatients: number;
  totalAssetsUsed: number;
  perNationStats: Record<string, Record<string, number>> & { _breakdown?: ExconSnapshotBreakdown; _global?: Record<string, number> };
  backupFilename: string | null;
}

export interface ExconSnapshotsResponse {
  snapshots: ExconSnapshot[];
}

export const statisticsApi = {
  getDashboard: () => api.get<DashboardStatistics>('/api/statistics/dashboard'),
  getCapacityStats: () => api.get('/api/statistics/capacity'),
  getDetailed: () => api.get<DetailedStatistics>('/api/statistics/detailed'),
  getExerciseSummary: () => api.get('/api/statistics/exercise-summary'),
  getExerciseAnalytics: () => api.get<ExerciseAnalytics>('/api/statistics/exercise-analytics'),
  getNationReport: (nationCode: string) => api.get<NationReport>(`/api/statistics/nation-report/${nationCode}`),
  getDayComparison: () => api.get<{ exerciseDays: DayComparisonEntry[]; dayDeltas?: DayDelta[] }>('/api/statistics/day-comparison'),
  getNationAnalytics: () => api.get<NationAnalyticsResponse>('/api/statistics/nation-analytics'),
  exportNationAnalyticsPdf: (data: NationAnalyticsResponse) => api.post('/api/exports/nation-analytics/pdf', data, { responseType: 'blob' }),
  getDataConsistency: () => api.get<DataConsistencyResponse>('/api/statistics/data-consistency'),
  getExconDaily: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return api.get<ExconDailyStats>(`/api/statistics/excon-daily${qs ? `?${qs}` : ''}`);
  },
  getExconSnapshots: () => api.get<ExconSnapshotsResponse>('/api/statistics/excon-snapshots'),
};

// Settings API
export interface SystemSetting {
  id: string;
  key: string;
  value: string;
  type: 'BOOLEAN' | 'STRING' | 'NUMBER' | 'JSON';
  description?: string;
  category: string;
  updatedBy?: string;
  updatedByUser?: {
    username: string;
    nation: string;
    email: string;
  };
  updatedAt: string;
}

export const settingsApi = {
  getAll: () => api.get<SystemSetting[]>('/api/settings'),
  getByKey: (key: string) => api.get<SystemSetting>(`/api/settings/${key}`),
  update: (key: string, value: any) => api.put<SystemSetting>(`/api/settings/${key}`, { value }),
  create: (data: {
    key: string;
    value: any;
    type: 'BOOLEAN' | 'STRING' | 'NUMBER' | 'JSON';
    description?: string;
    category?: string;
  }) => api.post<SystemSetting>('/api/settings', data),
  getTrainingMode: () => api.get<{ enabled: boolean }>('/api/settings/public/training-mode'),
  setTrainingMode: (enabled: boolean, password: string) =>
    api.put<{ enabled: boolean; message: string }>('/api/settings/training-mode', { enabled, password }),
  getTransportRules: () => api.get<{ rules: TransportCompatibilityRule[] }>('/api/settings/public/transport-rules')
};

export interface TransportCompatibilityRule {
  transportMode: string;
  dependency: string;
  defaultSlot: string;
  alternativeSlot: string | null;
  alternativeMessage?: string;
}

export interface Backup {
  filename: string;
  size: number;
  created: string;
  type: 'full' | 'data-only' | 'unknown';
  isZip: boolean;
  recordCounts: Record<string, number>;
}

export interface BackupValidation {
  valid: boolean;
  filename: string;
  isZip: boolean;
  manifest: {
    timestamp?: string;
    version?: string;
    type?: 'full' | 'data-only';
    includesUsers?: boolean;
    includesSettings?: boolean;
    includesFiles?: boolean;
  };
  recordCounts: Record<string, number>;
  hasUploadedFiles: boolean;
  uploadedFileCount: number;
  checks: { label: string; status: 'ok' | 'warn' | 'fail'; detail: string }[];
  currentStats: {
    users: number;
    requests: number;
    patients: number;
    offers: number;
    missions: number;
  };
}

// User type for admin/settings
export interface User {
  id: string;
  email: string;
  nation: string;
  nationCode?: string;
  username?: string;
  role: string;
  flagImage?: string;
  isExcon?: boolean;
  isAdmin?: boolean;
  isNation?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Auth / Impersonation API
export interface SimulatedUser {
  id: string;
  nation: string;
  nationCode?: string;
  username?: string;
  displayName?: string;
  email: string;
  role: string;
  flagImage?: string;
  isSimulated?: boolean;
  isNation?: boolean;
  isEU?: boolean;
  isExcon?: boolean;
  isPlanner?: boolean;
  isHub?: boolean;
}

export const authApi = {
  getSimulatedUsers: () => api.get<SimulatedUser[]>('/api/auth/simulated-users'),
  forgotPassword: (data: { email: string; username: string }) => api.post('/api/auth/forgot-password', data)
};

export const adminApi = {
  createBackup: () => api.post('/api/admin/backup'),
  createFullBackup: () => api.post('/api/admin/backup/full'),
  deleteAllData: () => api.post('/api/admin/delete-all-data'),
  deleteUsers: (userIds: string[]) => api.post('/api/admin/delete-users', { userIds }),
  listBackups: () => api.get<{ backups: Backup[] }>('/api/admin/backups'),
  downloadBackup: (filename: string) => api.get(`/api/admin/backups/${filename}/download`, { responseType: 'blob' }),
  deleteBackup: (filename: string) => api.delete(`/api/admin/backups/${filename}`),
  validateBackup: (filename: string) => api.post<BackupValidation>(`/api/admin/backups/${filename}/validate`),
  restoreBackup: (filename: string) => api.post(`/api/admin/backups/${filename}/restore`),
  uploadBackup: (file: File) => {
    const formData = new FormData();
    formData.append('backup', file);
    return api.post<{ success: boolean; filename: string; size: number }>('/api/admin/backups/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getDeploymentStatus: () => api.get<DeploymentStatus>('/api/admin/deployment-status')
};

// Patient Management API
export const patientsApi = {
  // Get my nation's assignments (treatment + transport)
  getMyAssignments: (nation?: string) => api.get<any[]>('/api/patients/my-assignments', { params: nation ? { nation } : {} }),
  getMyTransportAssignments: (nation?: string) => api.get<any[]>('/api/patients/my-transport-assignments', { params: nation ? { nation } : {} }),

  // Get all patients (filtered by role)
  getAll: () => api.get<Patient[]>('/api/patients'),

  // Get single patient by ID
  getById: (id: string) => api.get<Patient>(`/api/patients/${id}`),

  // Get patients for a specific request
  getByRequestId: (requestId: string, params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    patientId?: string;
    status?: string;
    priority?: string;
    dependency?: string;
    transport?: string;
    requirements?: string[];
    hasCompanions?: boolean;
    diagnosisSearch?: string;
    search?: string;
  }) => api.get<{ patients: Patient[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/api/patients/request/${requestId}`, { params }),

  // Get patient statistics for a request
  getStatistics: (requestId: string) => api.get<{
    total: number;
    byStatus: { CIVIL: number; MILITARY: number };
    byPriority: { P1: number; P2: number; P3: number };
    byDependency: { LOW: number; MEDIUM: number; HIGH: number };
    byTransport: { SEATED: number; LYING: number };
    withCompanions: number;
  }>(`/api/patients/request/${requestId}/statistics`),

  // Import patients (ExCon/Admin only)
  import: (formData: FormData) => api.post('/api/patients/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Link patients to request via Excel upload (Nation only)
  linkToRequest: (requestId: string, formData: FormData) =>
    api.post(`/api/patients/link-to-request/${requestId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Unlink patient from request (Nation only)
  unlinkFromRequest: (requestId: string, patientId: string) =>
    api.delete(`/api/patients/${patientId}/unlink-from-request/${requestId}`),

  // DEPRECATED: Preview patients by GUIDs from Excel
  previewByGuids: (formData: FormData) =>
    api.post<Patient[]>('/api/patients/preview-by-guids', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // NEW: Preview Excel file (parse without saving)
  previewExcel: (formData: FormData) =>
    api.post<{
      patients: any[];
      errors: Array<{ row: number; error: string }>;
      summary: {
        total: number;
        errors: number;
        civil: number;
        military: number;
        p1: number;
        p2: number;
        p3: number;
        seated: number;
        lying: number;
        withCompanions: number;
      };
    }>('/api/patients/preview-excel', formData),

  // Download patient document
  downloadDocument: (patientId: string, documentId: string) =>
    api.get(`/api/patients/${patientId}/documents/${documentId}`, {
      responseType: 'blob'
    }),

  // Update request exit point
  updateExitPoint: (requestId: string, exitPointId: string) =>
    api.patch(`/api/requests/${requestId}/exit-point`, { exitPointId }),

  // Treatment planning — assign patient to treatment hub
  assignTreatmentHub: (patientId: string, hubId: string) =>
    api.put(`/api/patients/${patientId}/treatment-planning`, { hubId }),

  // Treatment planning — remove treatment hub assignment
  removeTreatmentHub: (patientId: string) =>
    api.delete(`/api/patients/${patientId}/treatment-planning`),

  // Treatment planning — get available hubs for nation
  getTreatmentHubs: (nation?: string) =>
    api.get<TreatmentHub[]>('/api/patients/treatment-planning/hubs', { params: nation ? { nation } : {} }),
};

export interface TreatmentHub {
  id: string;
  name: string;
  type: string;
  nation: string;
  capacity: number | null;
  offeredTreatments: string[];
  latitude: number;
  longitude: number;
}

export type PatientTreatmentStatus = 'UNASSIGNED' | 'ASSIGNED' | 'PLANNED' | 'COMPLETED';
export type PatientTransportStatus = 'UNASSIGNED' | 'ASSIGNED' | 'PLANNED' | 'IN_TRANSIT' | 'COMPLETED';

// Assignment System Types
export type MatchType = 'PERFECT' | 'COMBINED' | 'PARTIAL' | 'NONE';
export type AssignmentStatus = 'DRAFT' | 'ACCEPTED' | 'REJECTED';

export interface OfferInfo {
  id: string;
  userId: string;
  nation: string;
  flagImage: string | null;
  offerType: OfferType;
  transportProviderNationId?: string | null;
  transportMode?: string | null;
  comment?: string | null;
  receivingStartPoint: {
    id: string;
    name: string;
  } | null;
}

export interface PatientMatchAnalysis {
  patientId: string;
  patient: {
    id: string;
    patientId: string | null;
    patientInitials: string;
    patientAge: number;
    patientGender: string;
    patientStatus: string;
    medicalPrimaryConditionId: string;
    medicalPrimaryConditionDiagnosis: string;
    medicalPriority: string;
    medicalTransport: string;
    medicalDependency: string;
    patientCompanionsCount: number;
  };
  matchTypes: {
    type: MatchType;
    count: number;
  }[];
  offers: {
    treatment: OfferInfo[];
    transport: OfferInfo[];
    both: OfferInfo[];
  };
  currentAssignment?: {
    id: string;
    treatmentOffer: OfferInfo | null;
    transportOffer: OfferInfo | null;
    status: AssignmentStatus;
  };
}

export interface OfferAnalysis {
  requestId: string;
  requestTitle: string;
  patientAnalyses: PatientMatchAnalysis[];
}

export interface PatientAssignment {
  id: string;
  requestId: string;
  patientId: string;
  patient: {
    id: string;
    patientId: string | null;
    patientInitials: string;
  };
  treatmentOfferId?: string;
  treatmentOffer?: Offer;
  transportOfferId?: string;
  transportOffer?: Offer;
  status: AssignmentStatus;
  createdAt: string;
  updatedAt: string;
  acceptedAt?: string;
}

export interface AssignmentDraft {
  patientId: string;
  treatmentOfferId?: string;
  transportOfferId?: string;
}

// Assignment Management API
export const assignmentsApi = {
  // Analyze offers and get match types for all patients in a request
  analyzeOffers: (requestId: string) =>
    api.get<OfferAnalysis>(`/api/assignments/request/${requestId}/analyze`),

  // Save draft assignments
  saveDraft: (requestId: string, assignments: AssignmentDraft[]) =>
    api.post(`/api/assignments/request/${requestId}/draft`, { assignments }),

  // Get draft assignments for a request
  getDraft: (requestId: string) =>
    api.get<PatientAssignment[]>(`/api/assignments/request/${requestId}/draft`),

  // Accept final assignments (convert drafts to accepted)
  acceptAssignments: (requestId: string) =>
    api.post(`/api/assignments/request/${requestId}/accept`),

  // Delete draft assignment for a patient
  deleteDraft: (patientId: string) =>
    api.delete(`/api/assignments/patient/${patientId}/draft`),

  // Get locked patient IDs (those with ACCEPTED assignments)
  getLockedPatients: (requestId: string) =>
    api.get<LockedPatient[]>(`/api/assignments/request/${requestId}/locked-patients`)
};

// Locked Patient type
export interface LockedPatient {
  patientId: string;
  treatmentNation: string | null;
  transportNation: string | null;
  mission: {
    missionId: string;
    nation: string;
  } | null;
}

// Mission System Types
export type MissionStatus = 'DRAFT' | 'PLANNED' | 'ACTIVE' | 'IN_TRANSIT' | 'COMPLETED' | 'CANCELLED';
export type MissionType = 'BOTH' | 'TRANSPORT' | 'TREATMENT';

export interface MissionStopPatient {
  id: string;
  missionStopId: string;
  missionPatientId: string;
  treatmentHubId: string | null;
  missionPatient: {
    id: string;
    patient: {
      id: string;
      patientId: string | null;
      patientInitials: string;
    };
  };
  treatmentHub?: {
    id: string;
    name: string;
    nation: string;
    capacity: number;
    offeredTreatments: string[];
  } | null;
  finalDestinationHubId?: string;
  finalDestinationHub?: Hub;
}

export interface MissionStop {
  id: string;
  missionId: string;
  startPointId: string;
  sequence: number;
  arrivalTime: string | null;
  departureTime: string | null;
  startPoint: {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    ucpmClassification: string;
    types: string[];
    nation?: string;
  };
  estimatedArrival?: string;
  estimatedDeparture?: string;
  stopPatients: MissionStopPatient[];
}

export interface MissionPatient {
  id: string;
  missionId: string;
  patientId: string;
  patient: {
    id: string;
    patientId: string | null;
    patientInitials: string;
    patientAge?: number;
    patientGender?: string;
    patientStatus?: string;
    medicalPriority?: string;
    medicalTransport?: string;
    medicalDependency?: string;
    medicalPrimaryConditionId?: string;
    medicalPrimaryConditionDiagnosis?: string;
    patientCompanionsCount?: number;
    medicalRequirements?: { id: string; requirementType: string }[];
  };
  assignment?: {
    id: string;
    status: string;
    treatmentOfferId?: string | null;
    transportOfferId?: string | null;
    treatmentOffer?: {
      id: string;
      offerType: string;
      user: { nation: string; nationCode?: string; flagImage?: string | null };
    } | null;
    transportOffer?: {
      id: string;
      offerType: string;
      user: { nation: string; nationCode?: string; flagImage?: string | null };
    } | null;
  };
}

export interface Mission {
  id: string;
  missionId: string;
  requestId: string;
  nation: string;
  nationCode: string | null;
  userId: string;
  offerId: string;
  status: MissionStatus;
  missionType: MissionType;
  linkedMissionId: string | null;
  parentMissionId: string | null;
  assetId: string | null;
  departureTime: string | null;
  arrivalTime: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  request?: {
    id: string;
    requestId: string;
    title: string;
    status: string;
    category: string;
    readyAtHub?: string;
    exitPoint?: any;
    creator: { id: string; nation: string; flagImage?: string };
  };
  user?: { id: string; nation: string; flagImage?: string; email?: string };
  offer?: { id: string; offerType: OfferType; status: OfferStatus; receivingStartPoint?: any; comment?: string };
  patients?: MissionPatient[];
  stops?: MissionStop[];
  linkedMission?: Mission;
  linkedByMission?: Mission;
  parentMission?: { id: string; missionId: string; nation: string; status: string };
  childMissions?: { id: string; missionId: string; nation: string; missionType: string; status: string }[];
  asset?: { id: string; name: string; type: string; capacitySeated?: number; capacityLying?: number; capacityIntensive?: number };
  totalDistanceKm?: number;
  estimatedArrivalTime?: string;
  requestAutoCompleted?: boolean;
}

export const missionsApi = {
  getAll: (params?: { requestId?: string; status?: string }) =>
    api.get<Mission[]>('/api/missions', { params }),
  getById: (id: string) => api.get<Mission>(`/api/missions/${id}`),
  getByRequestId: (requestId: string) =>
    api.get<Mission[]>(`/api/missions/request/${requestId}`),
  updateStatus: (id: string, status: MissionStatus) =>
    api.put<Mission>(`/api/missions/${id}/status`, { status }),
  update: (id: string, data: { notes?: string; assetId?: string | null; departureTime?: string | null; arrivalTime?: string | null }) =>
    api.put<Mission>(`/api/missions/${id}`, data),

  // Stops
  addStop: (missionId: string, data: { startPointId: string; arrivalTime?: string; departureTime?: string }) =>
    api.post<MissionStop>(`/api/missions/${missionId}/stops`, data),
  updateStop: (missionId: string, stopId: string, data: { startPointId?: string; arrivalTime?: string | null; departureTime?: string | null; sequence?: number }) =>
    api.put<MissionStop>(`/api/missions/${missionId}/stops/${stopId}`, data),
  deleteStop: (missionId: string, stopId: string) =>
    api.delete(`/api/missions/${missionId}/stops/${stopId}`),

  // Stop patient assignments
  assignPatientsToStop: (missionId: string, stopId: string, missionPatientIds: string[]) =>
    api.post(`/api/missions/${missionId}/stops/${stopId}/patients`, { missionPatientIds }),
  removePatientFromStop: (missionId: string, stopId: string, missionPatientId: string) =>
    api.delete(`/api/missions/${missionId}/stops/${stopId}/patients/${missionPatientId}`),

  // Reorder stops
  reorderStops: (missionId: string, stopIds: string[]) =>
    api.put<Mission>(`/api/missions/${missionId}/stops/reorder`, { stopIds }),

  // Treatment hub assignment
  setTreatmentHub: (missionId: string, stopPatientId: string, treatmentHubId: string | null) =>
    api.put<MissionStopPatient>(`/api/missions/${missionId}/stop-patients/${stopPatientId}/treatment-hub`, { treatmentHubId }),

  // Split (all-or-nothing)
  split: (missionId: string, splits: { missionPatientIds: string[] }[]) =>
    api.post(`/api/missions/${missionId}/split`, { splits }),

  // Create sub-mission (incremental)
  createSubMission: (missionId: string, missionPatientIds: string[]) =>
    api.post<{ parent: Mission; child: Mission }>(`/api/missions/${missionId}/create-sub-mission`, { missionPatientIds }),

  // Move patients between missions
  movePatients: (sourceMissionId: string, missionPatientIds: string[], targetMissionId: string) =>
    api.post<{ source: Mission; target: Mission }>(`/api/missions/${sourceMissionId}/move-patients`, { missionPatientIds, targetMissionId }),

  // Delete a child mission (transport)
  deleteTransport: (missionId: string) =>
    api.delete(`/api/missions/${missionId}`),

  // ETA calculation
  calculateETA: (id: string, data?: { departureTime?: string; averageSpeedKmh?: number; stopDurationMinutes?: number }) =>
    api.post<{ mission: Mission; stopETAs: ETAResult[] }>(`/api/missions/${id}/calculate-eta`, data || {}),

  // Patient destination assignment
  setPatientDestination: (missionId: string, stopId: string, missionPatientId: string, finalDestinationHubId: string | null) =>
    api.put(`/api/missions/${missionId}/stops/${stopId}/patient-destination`, { missionPatientId, finalDestinationHubId }),

  // Pending transport backlog grouped by UCPM transit hub
  getPendingTransportBacklog: () =>
    api.get<{ hubId: string; hubName: string; ucpmClassification: string; patientCount: number; missionCount: number }[]>(
      '/api/missions/pending-transport-backlog'
    ),

  // Create a new mission from an accepted offer
  create: (data: { offerId: string; missionType?: 'BOTH' | 'TRANSPORT' | 'TREATMENT' }) =>
    api.post<Mission>('/api/missions', data),
};

// =====================================================
// Asset Template System
// =====================================================

export interface AssetTemplate {
  id: string;
  name: string;
  type: 'TRAIN' | 'SHIP' | 'AIRCRAFT' | 'BUS' | 'AMBULANCE' | 'DRONE' | 'HELICOPTER' | 'FERRY' | 'OTHER';
  capacitySeated: number;
  capacityLying: number;
  capacityIntensive: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { dayTemplateEntries: number };
}

export const assetTemplatesApi = {
  getAll: (params?: { type?: string; isActive?: boolean }) =>
    api.get<AssetTemplate[]>('/api/asset-templates', { params }),
  getById: (id: string) => api.get<AssetTemplate>(`/api/asset-templates/${id}`),
  create: (data: Partial<AssetTemplate>) => api.post<AssetTemplate>('/api/asset-templates', data),
  update: (id: string, data: Partial<AssetTemplate>) => api.put<AssetTemplate>(`/api/asset-templates/${id}`, data),
  delete: (id: string) => api.delete(`/api/asset-templates/${id}`),
  importTemplates: (file: File) => {
    const formData = new FormData();
    formData.append('templatesExcel', file);
    return api.post('/api/asset-templates/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// =====================================================
// Day Template System
// =====================================================

export interface DayTemplateEntry {
  id: string;
  dayTemplateId: string;
  assetTemplateId: string;
  assetTemplate: AssetTemplate;
  nation: string;
  nationCode?: string;
  quantity: number;
  createdAt: string;
}

export interface DayTemplate {
  id: string;
  name: string;
  description?: string;
  status: 'DRAFT' | 'READY';
  entries: DayTemplateEntry[];
  _count?: { exerciseDays: number };
  createdAt: string;
  updatedAt: string;
}

export interface DayTemplateEntryInput {
  assetTemplateId: string;
  nation: string;
  nationCode?: string;
  quantity: number;
}

export const dayTemplatesApi = {
  getAll: (params?: { status?: string }) =>
    api.get<DayTemplate[]>('/api/day-templates', { params }),
  getById: (id: string) => api.get<DayTemplate>(`/api/day-templates/${id}`),
  create: (data: { name: string; description?: string; entries?: DayTemplateEntryInput[] }) =>
    api.post<DayTemplate>('/api/day-templates', data),
  update: (id: string, data: { name?: string; description?: string; status?: string; entries?: DayTemplateEntryInput[] }) =>
    api.put<DayTemplate>(`/api/day-templates/${id}`, data),
  delete: (id: string) => api.delete(`/api/day-templates/${id}`),
  importEntries: (templateId: string, file: File) => {
    const formData = new FormData();
    formData.append('entriesExcel', file);
    return api.post(`/api/day-templates/${templateId}/import-entries`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

export const dayTemplatePermissionsApi = {
  getPermissions: (templateId: string) =>
    api.get<DayTemplateNationPermission[]>(`/api/day-templates/${templateId}/permissions`),
  updatePermissions: (templateId: string, permissions: { nation: string; canCreateAssets: boolean; allowedAssetTypes: string[] }[]) =>
    api.put<DayTemplateNationPermission[]>(`/api/day-templates/${templateId}/permissions`, { permissions }),
  getActivePermissions: () =>
    api.get<DayTemplateNationPermission[]>('/api/day-templates/active-permissions'),
  checkPermission: (nation: string, assetType: string) =>
    api.post<{ allowed: boolean; reason?: string }>('/api/day-templates/check-permission', { nation, assetType }),
};

// =====================================================
// Exercise Day System
// =====================================================

export interface ExerciseDay {
  id: string;
  dayLabel: string;
  dayTemplateId: string;
  dayTemplate: { id: string; name: string };
  status: 'ACTIVE' | 'COMPLETED';
  activatedAt: string;
  activatedBy: string;
  completedAt?: string;
  snapshot?: DaySnapshot;
  createdAt: string;
}

export interface DaySnapshot {
  id: string;
  exerciseDayId: string;
  dayLabel: string;
  templateName: string;
  totalRequests: number;
  totalOffers: number;
  totalMissions: number;
  totalPatients: number;
  totalAssetsUsed: number;
  perNationStats?: Record<string, any>;
  backupFilename?: string;
  createdAt: string;
}

export interface SystemStatus {
  maintenanceMode: boolean;
  activeExerciseDay: {
    id: string;
    dayLabel: string;
    templateName: string;
    activatedAt: string;
  } | null;
}

export interface PreflightCheck {
  id: string;
  label: string;
  status: 'ok' | 'warn' | 'fail';
  detail: string;
}

export interface PreflightResult {
  ready: boolean;
  hasWarnings: boolean;
  checks: PreflightCheck[];
}

export interface DeploymentStatus {
  healthy: boolean;
  hasWarnings: boolean;
  checks: PreflightCheck[];
  backupFiles: { name: string; size: string; date: string }[];
  timestamp: string;
}

// ─── Inject Control ───────────────────────────────────────────────────────────

export type InjectScreenType = 'SCREEN_404' | 'SCREEN_SERVER_DOWN';
export type InjectTargetType = 'ALL' | 'NATIONS' | 'ROLES' | 'USERS';

export interface Inject {
  id: string;
  name: string;
  description?: string | null;
  screenType: InjectScreenType;
  targetType: InjectTargetType;
  targetValues: string[];
  startTime: string;
  endTime?: string | null;
  durationMins?: number | null;
  isEnabled: boolean;
  isCurrentlyActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface InjectCheckResult {
  locked: boolean;
  screenType: InjectScreenType | null;
  injectName: string | null;
  injectId: string | null;
}

export interface CreateInjectData {
  name: string;
  description?: string;
  screenType: InjectScreenType;
  targetType: InjectTargetType;
  targetValues: string[];
  startTime: string;
  endTime?: string | null;
  durationMins?: number | null;
  isEnabled: boolean;
}

export const injectsApi = {
  getAll: () => api.get<Inject[]>('/api/injects'),
  check: () => api.get<InjectCheckResult>('/api/injects/check'),
  create: (data: CreateInjectData) => api.post<Inject>('/api/injects', data),
  update: (id: string, data: Partial<CreateInjectData>) => api.put<Inject>(`/api/injects/${id}`, data),
  remove: (id: string) => api.delete<{ success: boolean }>(`/api/injects/${id}`),
};

export const exerciseApi = {
  getStatus: () => api.get<SystemStatus>('/api/exercise/status'),
  getDays: () => api.get<ExerciseDay[]>('/api/exercise/days'),
  preflight: (dayTemplateId?: string) =>
    api.get<PreflightResult>('/api/exercise/preflight', { params: dayTemplateId ? { dayTemplateId } : {} }),
  activate: (data: { dayTemplateId: string; dayLabel: string }) =>
    api.post('/api/exercise/activate', data),
  mergeActivate: (data: { dayTemplateId: string; dayLabel: string }) =>
    api.post('/api/exercise/merge-activate', data),
  reset: () => api.post('/api/exercise/reset')
};

// Notification types
export interface AppNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdAt: string;
}

export const notificationsApi = {
  getAll: (params?: { unreadOnly?: boolean; limit?: number; offset?: number; type?: string }) =>
    api.get<{ notifications: AppNotification[]; total: number; unreadCount: number }>('/api/notifications', { params }),
  getUnreadCount: () => api.get<{ count: number }>('/api/notifications/unread-count'),
  markAsRead: (id: string) => api.patch(`/api/notifications/${id}/read`),
  markAllAsRead: () => api.post('/api/notifications/mark-all-read'),
  delete: (id: string) => api.delete(`/api/notifications/${id}`)
};

// =====================================================
// Sync API
// =====================================================

export interface SyncSettings {
  enabled: boolean;
  targetUrl: string;
  proxyHost: string;
  proxyPort: number;
  proxyUsername: string;
  hasProxyPassword: boolean;
  intervalSeconds: number;
  maxRetries: number;
  hasApiKey: boolean;
}

export interface SyncOutboxEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  status: 'PENDING' | 'SENDING' | 'SENT' | 'FAILED';
  attempts: number;
  maxRetries: number;
  errorMessage: string | null;
  sentAt: string | null;
  createdAt: string;
}

export interface SyncStatus {
  pending: number;
  sending: number;
  sent: number;
  failed: number;
  total: number;
  lastSentAt: string | null;
  recentEntries: SyncOutboxEntry[];
}

export interface UserDetails {
  id: string;
  username: string;
  displayName: string;
  firstName: string;
  lastName: string;
  function: string;
  institution: string;
  nation: string;
  nationCode: string;
  email: string;
  role: string;
  containerAddress: string;
  isSimulated: boolean;
  flagImage: string;
  createdAt: string;
  lastLoginAt: string;
  mustChangePassword: boolean;
  isNation: boolean;
  isEU: boolean;
  isAdmin: boolean;
  isExcon: boolean;
  isPlanner: boolean;
  isHub: boolean;
  isReadOnly: boolean;
  isDeleted: boolean;
  activity: {
    requestsCreated: number;
    offersMade: number;
    commentsWritten: number;
    lastLogin: string | null;
  };
  hubAssignments: { id: string; name: string; type: string; nation: string }[];
  recentAuditLogs: { id: string; action: string; resourceType: string; resourceId: string; createdAt: string; details: string }[];
}

export interface ExerciseAnalytics {
  generatedAt: string;
  overview: {
    totalRequests: number;
    completedRequests: number;
    activeRequests: number;
    withdrawnRequests: number;
    closedRequests: number;
    totalOffers: number;
    acceptedOffers: number;
    totalPatients: number;
    evacuatedPatients: number;
    inTransitPatients: number;
    waitingPatients: number;
    totalMissions: number;
    completedMissions: number;
    totalSchedules: number;
    totalBookings: number;
    totalUsers: number;
    totalAssets: number;
    totalHubs: number;
    nationsParticipating: number;
  };
  performanceScores: {
    overallCompletionRate: number;
    offerAcceptanceRate: number;
    patientEvacuationRate: number;
    avgResponseTimeHours: number | null;
  };
  responseTimesPerNation: {
    nation: string;
    nationCode: string;
    avgHours: number;
    minHours: number;
    maxHours: number;
    offerCount: number;
  }[];
  nationParticipation: {
    nation: string;
    nationCode: string;
    flagImage: string;
    requestsCreated: number;
    patientsRequested: number;
    offersSubmitted: number;
    offersAccepted: number;
    patientsOffered: number;
    usersCount: number;
    commentsCount: number;
    schedulesProvided: number;
    assetsProvided: number;
  }[];
  activityTimeline: { date: string; requests: number; offers: number; comments: number; auditActions: number }[];
  bottlenecks: {
    requestsWithNoOffers: number;
    requestsStuckInReview: number;
    longWaitRequests: number;
    longWaitRequestIds: { id: string; requestId: string; title: string; nation: string }[];
  };
  patientAnalysis: {
    total: number;
    evacuated: number;
    inTransit: number;
    waiting: number;
    byPriority: Record<string, { total: number; evacuated: number; inTransit: number; waiting: number }>;
    byTransportMode?: { mode: string; count: number }[];
    evacuationRate: number;
  };
  infrastructure: {
    totalAssetCapacity: number;
    totalHubCapacity: number;
    assetsCount: number;
    hubsCount: number;
    schedulesActive: number;
  };
  requestStatusDistribution: { status: string; count: number; percentage: number }[];
  cumulativeProgress: { date: string; totalRequests: number; totalOffers: number; totalCompleted: number; totalPatients: number }[];
  offerTypeDistribution: { type: string; count: number }[];
  trendProjections: {
    projectedRequestsNextWeek: number;
    projectedOffersNextWeek: number;
    requestTrend: 'increasing' | 'decreasing' | 'stable';
    offerTrend: 'increasing' | 'decreasing' | 'stable';
    avgDailyRequests: number;
    avgDailyOffers: number;
    peakActivityDay: string;
    peakActivityCount: number;
  };
  nationRadarData: { nation: string; requests: number; offers: number; patients: number; assets: number; responsiveness: number; completion: number }[];
  hourlyActivityHeatmap: { dayOfWeek: number; hour: number; count: number }[];
  categoryDistribution?: { category: string; count: number; percentage: number }[];
  missionAnalysis?: {
    total: number;
    byStatus: { status: string; count: number }[];
    completedToday: number;
    completionRate: number;
  };
  actionSummary?: { action: string; count: number }[];
  pipelineTimings: {
    avgReviewTimeHours: number | null;
    avgResponseTimeHours: number | null;
    avgSelectionTimeHours: number | null;
    avgEndToEndHours: number | null;
    medianEndToEndHours: number | null;
    avgPatientsPerRequest: number;
    avgOffersPerRequest: number;
    requestToMissionConversion: number;
    fastestCompletionHours: number | null;
    slowestCompletionHours: number | null;
  };
  nationPerformanceCards: {
    nation: string;
    nationCode: string;
    flagImage: string;
    metrics: {
      requestsCreated: number;
      requestsCompleted: number;
      completionRate: number;
      patientsRequested: number;
      patientsEvacuated: number;
      evacuationRate: number;
      offersSubmitted: number;
      offersAccepted: number;
      offerAcceptanceRate: number;
      avgResponseTimeHours: number | null;
      missionsCreated: number;
      missionsCompleted: number;
      assetsProvided: number;
      totalAssetCapacity: number;
      schedulesProvided: number;
      commentsCount: number;
      avgPatientsPerRequest: number;
      totalDistanceKm: number;
      // Enhanced per-nation metrics
      auditActionCount: number;
      patientsWaiting: number;
      patientsInTransit: number;
      offersPending: number;
      requestsByStatus: Record<string, number>;
      avgReviewHours: number | null;
      avgSelectionHours: number | null;
      avgEndToEndHours: number | null;
      staleDrafts: number;
      patientsWithoutOffer: number;
    };
    rankings: {
      responseSpeed: number;
      volume: number;
      efficiency: number;
      overall: number;
    };
    commentary: string[];
  }[];
  featureUsage: { feature: string; count: number }[];
  patientFlow: {
    byDependency: { level: string; count: number }[];
    totalCompanions: number;
    avgEvacuationTimeHours: number | null;
    medianEvacuationTimeHours: number | null;
    fastestEvacuationHours: number | null;
    slowestEvacuationHours: number | null;
  };
  missionPerformance: {
    avgDurationHours: number | null;
    totalDistanceKm: number;
    byType: { type: string; count: number }[];
    byNation: { nation: string; total: number; completed: number; completionRate: number }[];
  };
  dataChecks: {
    severity: 'critical' | 'warning' | 'info';
    category: string;
    message: string;
    count: number;
    nation?: string;
    details?: string[];
  }[];
}

export interface DayComparisonEntry {
  id: string;
  dayLabel: string;
  templateName: string;
  templateId: string;
  status: string;
  activatedAt: string;
  completedAt: string | null;
  snapshot: {
    totalRequests: number;
    totalOffers: number;
    totalMissions: number;
    totalPatients: number;
    totalAssetsUsed: number;
    perNationStats: Record<string, {
      missions?: number; assets?: number;
      requests?: number; requestsCompleted?: number;
      patients?: number; patientsEvacuated?: number;
      offers?: number; offersAccepted?: number;
      missionsCompleted?: number; avgResponseHours?: number | null;
      totalDistanceKm?: number;
    }>;
    backupFilename: string;
    completedRequests?: number | null;
    acceptedOffers?: number | null;
    evacuatedPatients?: number | null;
    completedMissions?: number | null;
    completionRate?: number | null;
    evacuationRate?: number | null;
    offerAcceptanceRate?: number | null;
    avgResponseHours?: number | null;
  } | null;
}

export interface DayDelta {
  fromDay: string;
  toDay: string;
  requestsDelta: number;
  offersDelta: number;
  missionsDelta: number;
  patientsDelta: number;
  completionRateDelta: number | null;
  evacuationRateDelta: number | null;
}

export interface NationReport {
  nationCode: string;
  nationName: string;
  flagImage: string;
  users: { id: string; name: string; email: string; lastLogin: string | null }[];
  summary: {
    usersCount: number;
    requestsCreated: number;
    totalPatients: number;
    offersSubmitted: number;
    offersAccepted: number;
    commentsWritten: number;
    missionsCreated: number;
    assetsProvided: number;
    schedulesCreated: number;
  };
  requestsByStatus: Record<string, number>;
  offersByStatus: Record<string, number>;
  responseTime: { avgHours: number | null; minHours: number | null; maxHours: number | null; medianHours?: number | null };
  performanceScores?: { completionRate: number; evacuationRate: number; offerAcceptanceRate: number };
  patientsByPriority: Record<string, number>;
  patientsByStatus: Record<string, number>;
  patientsByTransportMode?: Record<string, number>;
  patientsByDependency?: Record<string, number>;
  patientFlow?: {
    totalCompanions: number;
    evacuated: number;
    avgEvacuationTimeHours: number | null;
    medianEvacuationTimeHours: number | null;
  };
  missionDetails?: {
    byStatus: Record<string, number>;
    totalDistanceKm: number;
    avgDurationHours: number | null;
    completed: number;
  };
  activityTimeline?: { date: string; actions: number }[];
  commentary?: string[];
  assets: { name: string; type: string; totalCapacity: number }[];
  recentRequests: any[];
  recentOffers: any[];
}

export interface NationHealthIssue {
  key: string;
  label: string;
  description: string;
  count: number;
  severity: 'ok' | 'warning' | 'error';
}

export interface NationAnalyticsEntry {
  nation: string;
  nationCode: string;
  requests: {
    total: number;
    byStatus: Record<string, number>;
    totalPatients: number;
    totalOffersReceived: number;
  };
  offersMade: {
    total: number;
    byStatus: Record<string, number>;
  };
  offersReceived: {
    total: number;
    byStatus: Record<string, number>;
  };
  patients: {
    total: number;
    byPriority: Record<string, number>;
    withMission: number;
    withAssignment: number;
    unmanaged: number;
  };
  missionsProvided: {
    total: number;
    byStatus: Record<string, number>;
  };
  missionsOnRequests: {
    total: number;
    byStatus: Record<string, number>;
  };
  chains: {
    total: number;
    accepted: number;
  };
  assets: {
    total: number;
    active: number;
    byType: Record<string, number>;
    totalCapacity: number;
  };
  healthIssues: NationHealthIssue[];
  lessonsLearned: {
    avgResponseTimeHours: number | null;
    avgOfferResponseHours: number | null;
    completionRate: number | null;
    offerAcceptanceRate: number | null;
    evacuationRate: number | null;
    missionCompletionRate: number | null;
    commentary: string[];
  };
}

export interface NationAnalyticsResponse {
  generatedAt: string;
  exerciseDay: { label: string; activatedAt: string } | null;
  overallHealth: 'healthy' | 'warnings' | 'issues';
  totalIssues: number;
  nations: NationAnalyticsEntry[];
  globalHealth: NationHealthIssue[];
}

export interface DataConsistencyCheck {
  key: string;
  label: string;
  description: string;
  count: number;
  severity: 'ok' | 'warning' | 'error';
}

export interface DataConsistencyNationUsage {
  requests: number;
  offers: number;
  missions: number;
  patients: number;
  comments: number;
  logins: number;
}

export interface DataConsistencyNationBreakdown {
  nation: string;
  totalIssues: number;
  usage: DataConsistencyNationUsage;
  checks: Record<string, number>;
}

export interface DataConsistencyResponse {
  generatedAt: string;
  overallStatus: 'healthy' | 'warnings' | 'issues';
  totalIssues: number;
  totalChecks: number;
  checks: DataConsistencyCheck[];
  nationBreakdown: DataConsistencyNationBreakdown[];
}

export interface BackupInfo {
  filename: string;
  size: number;
  created: string;
}

export const transportPlansApi = {
  getAll: (params?: { nation?: string; requestId?: string }) =>
    api.get<any[]>('/api/transport-plans', { params }),
  save: (data: any) => api.post<any>('/api/transport-plans', data),
  delete: (id: string) => api.delete(`/api/transport-plans/${id}`),
  finalize: (id: string) => api.post<any>(`/api/transport-plans/${id}/finalize`),
};

export const syncApi = {
  getSettings: () => api.get<SyncSettings>('/api/sync/settings'),
  updateSettings: (data: any) => api.put<{ message: string }>('/api/sync/settings', data),
  testConnection: () => api.post<{ message: string; remote?: any }>('/api/sync/test'),
  getStatus: () => api.get<SyncStatus>('/api/sync/status'),
  retryEntry: (id: string) => api.post(`/api/sync/retry/${id}`),
  retryAll: () => api.post<{ message: string }>('/api/sync/retry-all'),
  purgeSent: (days: number) => api.delete<{ message: string }>(`/api/sync/purge-sent?days=${days}`)
};
